import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Lock } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Logo } from '@/shared/ui/logo';

/**
 * The passcode prompt.
 *
 * Shown when the lab put a passcode on the link, which they do for identifiable
 * cases - the link itself is unguessable, but a passcode means a forwarded
 * email is not access on its own.
 *
 * The wording matters here. Somebody reaching this screen has been sent a link
 * by a lab they work with and has been asked for a code they may not have to
 * hand. Telling them the link is invalid would be both wrong and alarming.
 */
const schema = Yup.object({
  passcode: Yup.string().trim().required('Enter the code the lab sent you.'),
});

export function PasscodeGate({
  onSubmit,
  error,
  pending,
}: {
  onSubmit: (passcode: string) => void;
  /** Set once a code has been tried and refused. */
  error?: string | undefined;
  pending: boolean;
}) {
  const formik = useFormik({
    initialValues: { passcode: '' },
    validationSchema: schema,
    onSubmit: (values) => {
      onSubmit(values.passcode.trim());
    },
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center">
            <Logo />
          </div>
          <div className="flex justify-center">
            <span
              className="flex size-10 items-center justify-center rounded-full bg-accent"
              aria-hidden="true"
            >
              <Lock className="size-4" />
            </span>
          </div>
          <CardTitle>This design is passcode protected</CardTitle>
          <CardDescription>
            The lab sent you a short code alongside the link. Enter it to open the design.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
            {error && (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="passcode">Passcode</Label>
              <Input
                id="passcode"
                autoFocus
                autoComplete="one-time-code"
                {...formik.getFieldProps('passcode')}
                aria-invalid={Boolean(formik.touched.passcode && formik.errors.passcode)}
              />
              {formik.touched.passcode && formik.errors.passcode && (
                <p className="text-sm text-destructive">{formik.errors.passcode}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={pending}>
              {pending ? 'Checking…' : 'Open the design'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
